import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, EmptyState, fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Plus, Factory } from "@phosphor-icons/react";

const empty = { name: "", contact_name: "", email: "", phone: "", country: "" };

export default function Suppliers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/suppliers").then((r) => setRows(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      await api.post("/suppliers", form);
      toast.success("Supplier added");
      setOpen(false);
      setForm(empty);
      load();
    } catch { toast.error("Failed"); }
  };

  const removeSupplier = async (id, name) => {
    await api.delete(`/suppliers/${id}`);
    toast.success(`Supplier ${name} deleted`);
    load();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        subtitle="Vendor directory"
        title="Suppliers"
        actions={<Button className="rounded-sm" onClick={() => setOpen(true)} data-testid="add-supplier-button"><Plus size={16} className="mr-2" /> Add Supplier</Button>}
      />
      {!loading && rows.length === 0 ? (
        <EmptyState title="No suppliers" hint="Add suppliers to raise purchase orders against them." action={<Button className="rounded-sm" onClick={() => setOpen(true)}>Add Supplier</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((s) => (
            <div key={s.id} className="hp-card p-5" data-testid={`supplier-card-${s.name}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2"><Factory size={18} className="text-primary" weight="duotone" /><p className="font-heading font-bold">{s.name}</p></div>
                <ConfirmDelete
                  testid={`delete-supplier-${s.name}`}
                  title={`Delete ${s.name}?`}
                  description="This permanently removes the supplier. This cannot be undone."
                  onConfirm={() => removeSupplier(s.id, s.name)}
                />
              </div>
              <div className="text-sm text-muted-foreground space-y-1">
                {s.contact_name && <p>{s.contact_name}</p>}
                {s.email && <p>{s.email}</p>}
                {s.phone && <p>{s.phone}</p>}
                {s.country && <p>{s.country}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Supplier</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs uppercase tracking-wide">Company name *</Label><Input className="mt-1.5 rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Contact</Label><Input className="mt-1.5 rounded-sm" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="supplier-contact" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Phone</Label><Input className="mt-1.5 rounded-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="supplier-phone" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Email</Label><Input className="mt-1.5 rounded-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="supplier-email" /></div>
            <div><Label className="text-xs uppercase tracking-wide">Country</Label><Input className="mt-1.5 rounded-sm" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="supplier-country" /></div>
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={save} data-testid="save-supplier">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
